// src/pages/ResetPasswordPage.js
import React, { useState } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle2, ArrowLeft, Loader2, HelpCircle } from 'lucide-react';
import { confirmPasswordReset } from '../api';

const ResetPasswordPage = () => {
  const { uid: paramUid, token: paramToken } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const uid = paramUid || searchParams.get('uid') || '';
  const token = paramToken || searchParams.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      await confirmPasswordReset({
        uid,
        token,
        new_password: newPassword,
      });
      setSuccess(true);
    } catch (err) {
      console.error('Password reset confirmation error:', err);
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.response?.data?.new_password) {
        const msg = Array.isArray(err.response.data.new_password)
          ? err.response.data.new_password[0]
          : err.response.data.new_password;
        setError(msg);
      } else {
        setError('This password reset link is invalid or has expired. Please request a new one or ask your coach for assistance.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="flex items-center justify-center min-h-screen bg-cover bg-center bg-no-repeat relative p-4"
      style={{ backgroundImage: "url('/images/login-bg.png')" }}
    >
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-gray-900/60" />

      {/* Form Container */}
      <div className="relative w-full max-w-md p-6 sm:p-8 space-y-6 bg-gray-900/10 backdrop-blur-md shadow-2xl rounded-2xl border border-white/20 z-10 mx-auto">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-3 bg-teal-500/20 border border-teal-500/30 rounded-full flex items-center justify-center text-teal-400">
            <Lock className="w-6 h-6" />
          </div>
          <h2 className="text-2xl font-bold text-white">Create New Password</h2>
          <p className="text-gray-300 text-sm mt-1">
            Choose a strong password with at least 8 characters.
          </p>
        </div>

        {success ? (
          <div className="space-y-5">
            <div className="bg-teal-500/10 border border-teal-500/30 rounded-xl p-4 text-center space-y-2">
              <CheckCircle2 className="w-8 h-8 text-teal-400 mx-auto" />
              <h3 className="font-semibold text-white text-base">Password Updated!</h3>
              <p className="text-gray-300 text-xs leading-relaxed">
                Your password has been successfully reset. You can now log in with your new credentials.
              </p>
            </div>

            <button
              type="button"
              onClick={() => navigate('/login')}
              className="w-full min-h-[44px] px-4 py-2.5 font-semibold text-gray-900 bg-gradient-to-r from-teal-400 to-teal-500 rounded-lg hover:from-teal-300 hover:to-teal-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-all shadow-lg"
            >
              Go to Login
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="new-password" className="block text-sm font-medium text-gray-100 mb-1">
                New Password
              </label>
              <div className="relative">
                <input
                  id="new-password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="w-full px-3.5 py-2.5 border border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-white bg-gray-800 pr-12 text-base"
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute inset-y-0 right-0 px-3 flex items-center justify-center text-gray-400 hover:text-white transition-colors focus:outline-none"
                  aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
                >
                  {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-100 mb-1">
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  id="confirm-password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-type new password"
                  className="w-full px-3.5 py-2.5 border border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-white bg-gray-800 pr-12 text-base"
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 px-3 flex items-center justify-center text-gray-400 hover:text-white transition-colors focus:outline-none"
                  aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-400 font-medium bg-red-500/10 border border-red-500/20 p-2.5 rounded-lg">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full min-h-[44px] px-4 py-2.5 font-semibold text-gray-900 bg-gradient-to-r from-teal-400 to-teal-500 rounded-lg hover:from-teal-300 hover:to-teal-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Updating Password...</span>
                </>
              ) : (
                <span>Reset Password</span>
              )}
            </button>
          </form>
        )}

        {/* Coach Assistance Fallback Notice */}
        <div className="bg-gray-800/80 border border-teal-500/40 rounded-xl p-4 space-y-2 shadow-md">
          <div className="flex items-start gap-2.5">
            <HelpCircle className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" />
            <div className="text-left space-y-1">
              <h4 className="font-semibold text-white text-xs sm:text-sm">
                Need coach assistance?
              </h4>
              <p className="text-gray-300 text-xs leading-relaxed">
                If this link has expired or you cannot access your account, your coach or club administrator can reset your credentials directly from their dashboard.
              </p>
            </div>
          </div>
        </div>

        {/* Back to Login Link */}
        <div className="text-center pt-1">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-sm text-teal-400 hover:text-teal-300 font-medium transition-colors p-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Login</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
